import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";

import {
  cancelEntryApi,
  deleteEntryApi,
  deleteSettlementApi,
  getEntryApi,
  getSettlementFileBlobApi,
} from "../api/financeiroApi";
import {
  ENTRY_SOURCE_LABELS,
  ENTRY_STATUS_BADGE,
  ENTRY_TYPE_LABELS,
  FinEntry,
  FinEntryDetail,
  FinSettlement,
  PAYMENT_METHOD_LABELS,
  isCompetenceOnlySource,
  isDpSource,
  statusLabel,
} from "../api/finTypes";
import SettlementModal from "../components/SettlementModal";
import DocumentFileButton from "../../dp/components/DocumentFileButton";
import ReasonModal from "../../dp/components/ReasonModal";
import { FINANCEIRO_PAGE, FIN_ENTRIES_PAGE, FIN_ENTRY_EDIT_PAGE, FIN_ENTRY_PAGE } from "../routes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { formatDocument, useFinPermissions } from "../utils/fin";
import { costCenterPath } from "../utils/costCenters";
import { DP_EMPLOYEE_TAB_PAGE, DP_FGTS_PAGE, DP_INSS_PAGE, DP_PAYROLL_PERIOD_PAGE, DP_PROVISIONS_PAGE } from "../../dp/routes";
import { isDpOrAdminFromToken } from "../../../utils/authToken";
import "../../dp/pages/Dp.css";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

type Confirm = { kind: "delete" } | { kind: "reverse"; settlement: FinSettlement } | null;

/** Parte da folha de que o título nasceu: adiantamento, líquido ou retenção repassada a terceiros. */
const FOLHA_PART_TEXT: Record<string, string> = {
  ADIANTAMENTO: "Adiantamento da competência",
  LIQUIDO: "Líquido da competência",
  CONSIGNADO: "Consignado descontado na competência (repasse aos bancos)",
  PENSAO: "Pensão alimentícia descontada na competência",
  IRRF: "IRRF retido na competência (DARF)",
};

/** Onde o título nasceu no DP (ADR 0029): o texto e a tela de lá. */
function dpOrigin(entry: FinEntry): { text: string; path: string | null } {
  const o = entry.origin;
  switch (entry.source) {
    case "FOLHA":
      return {
        text: FOLHA_PART_TEXT[o?.part ?? "LIQUIDO"] ?? "Líquido da competência",
        path: o?.periodId ? DP_PAYROLL_PERIOD_PAGE(o.periodId) : null,
      };
    case "FERIAS":
      return { text: "Recibo de férias", path: o?.employeeId ? DP_EMPLOYEE_TAB_PAGE(o.employeeId, "ferias") : null };
    case "RESCISAO":
      return {
        text: o?.part === "MULTA_FGTS" ? "Multa do FGTS do demonstrativo de rescisão" : "Demonstrativo de rescisão",
        path: o?.employeeId ? DP_EMPLOYEE_TAB_PAGE(o.employeeId, "contrato") : null,
      };
    case "GUIA_FGTS":
      return { text: "Guia de FGTS em Recolhimentos", path: DP_FGTS_PAGE() };
    case "GUIA_INSS":
      return { text: "DARF do INSS em Recolhimentos", path: DP_INSS_PAGE() };
    case "PROVISAO":
      return {
        text: "Sugestão de provisão do mês (13º, férias, 1/3 e FGTS), gravada no fechamento da folha do mês anterior. Só competência: entra na DRE, fica fora do fluxo de caixa e não tem baixa",
        path: DP_PROVISIONS_PAGE(),
      };
    default:
      return { text: ENTRY_SOURCE_LABELS[entry.source], path: null };
  }
}

export default function EntryDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token, canWrite } = useFinPermissions();

  const [entry, setEntry] = useState<FinEntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);

  const goBack = useGoBack(FIN_ENTRIES_PAGE(entry?.type ?? "PAGAR"));

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setEntry(await getEntryApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o lançamento.");
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    load();
  }, [load]);

  const runConfirm = async () => {
    if (!confirm || !entry) return;
    setBusy(true);
    try {
      if (confirm.kind === "delete") {
        await deleteEntryApi(token, entry.id);
        toast.success("Lançamento excluído.");
        navigate(FIN_ENTRIES_PAGE(entry.type), { replace: true });
        return;
      }
      await deleteSettlementApi(token, confirm.settlement.id);
      toast.success("Baixa estornada.");
      setConfirm(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar.");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !entry) {
    return (
      <div className="dpePage">
        <main className="dpePage__main">
          <p className="ecoEmpty">Carregando...</p>
        </main>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="dpePage">
        <main className="dpePage__main">
          <p className="ecoEmpty">Lançamento não encontrado.</p>
        </main>
      </div>
    );
  }

  const isReceivable = entry.type === "RECEBER";
  const isCanceled = entry.status === "CANCELADO";
  const hasOpen = Number(entry.openAmount) > 0;
  const hasSettlements = entry.settlements.length > 0;
  const fromDp = isDpSource(entry.source);
  const origin = dpOrigin(entry);
  // O link para o DP só aparece para quem abre o DP (ADMIN e DP); o Financeiro sozinho vê o texto.
  const canSeeDp = isDpOrAdminFromToken(token);

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Financeiro", onClick: () => navigate(FINANCEIRO_PAGE()) },
            {
              label: isReceivable ? "Contas a receber" : "Contas a pagar",
              onClick: () => navigate(FIN_ENTRIES_PAGE(entry.type)),
            },
            { label: entry.description },
          ]}
          title={`${entry.description}${entry.installmentNumber ? ` (${entry.installmentNumber}/${entry.installmentCount})` : ""}`}
          subtitle={
            <span className={`dpBadge ${ENTRY_STATUS_BADGE[entry.status]}`}>{statusLabel(entry.status, entry.type)}</span>
          }
          onBack={goBack}
          backLabel="Voltar"
          actions={
            canWrite && !isCanceled ? (
              <>
                {hasOpen && !isCompetenceOnlySource(entry.source) ? (
                  <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setSettling(true)}>
                    {isReceivable ? "Registrar recebimento" : "Registrar pagamento"}
                  </button>
                ) : null}
                <button type="button" className="ecoBtn" onClick={() => navigate(FIN_ENTRY_EDIT_PAGE(entry.id))}>
                  Editar
                </button>
              </>
            ) : undefined
          }
        />

        {isCanceled ? (
          <div className="dpNotice dpNotice--warning">
            Cancelado{entry.cancelReason ? `: ${entry.cancelReason}` : "."}
          </div>
        ) : null}

        <section className="finBox">
          <dl className="finFacts">
            <div>
              <dt>Tipo</dt>
              <dd>{ENTRY_TYPE_LABELS[entry.type]}</dd>
            </div>
            <div>
              <dt>Valor</dt>
              <dd className="finMoney">
                <strong>{formatCurrency(entry.amount)}</strong>
              </dd>
            </div>
            <div>
              <dt>{isReceivable ? "Recebido" : "Pago"}</dt>
              <dd className="finMoney">{formatCurrency(entry.settledAmount)}</dd>
            </div>
            <div>
              <dt>Em aberto</dt>
              <dd className="finMoney">{formatCurrency(entry.openAmount)}</dd>
            </div>
            <div>
              <dt>Vencimento</dt>
              <dd className={entry.isOverdue ? "finMoney--out" : ""}>{formatDateOnly(entry.dueDate)}</dd>
            </div>
            <div>
              <dt>Competência</dt>
              <dd>{formatDateOnly(entry.competenceDate)}</dd>
            </div>
            <div>
              <dt>Categoria</dt>
              <dd>{entry.category?.name ?? "—"}</dd>
            </div>
            <div>
              <dt>{isReceivable ? "Cliente" : "Fornecedor"}</dt>
              <dd>
                {entry.counterparty ? (
                  <>
                    {entry.counterparty.name}
                    {entry.counterparty.document ? (
                      <span className="finDesc__sub" style={{ display: "block" }}>{formatDocument(entry.counterparty.document)}</span>
                    ) : null}
                  </>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div>
              <dt>Centro de custo</dt>
              <dd>
                {entry.allocations.length ? (
                  <>
                    Rateado em {entry.allocations.length} centros
                    <table className="finAllocTable">
                      <tbody>
                        {entry.allocations.map((a) => (
                          <tr key={a.costCenter.id}>
                            <td>
                              {a.costCenter.code} — {a.costCenter.name}
                            </td>
                            <td className="finMoney">{formatCurrency(a.amount)}</td>
                            <td className="finDesc__sub">
                              {Number(entry.amount) > 0
                                ? `${((Number(a.amount) / Number(entry.amount)) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : entry.costCenter ? (
                  costCenterPath(entry.costCenter)
                ) : (
                  <span className="dpBadge dpBadge--orange">Sem centro de custo</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Loja</dt>
              <dd>{entry.store?.nomeFantasia ?? "—"}</dd>
            </div>
            {entry.source === "RECORRENCIA" ? (
              <div>
                <dt>Origem</dt>
                <dd>Repetição</dd>
              </div>
            ) : null}
            {fromDp ? (
              <div>
                <dt>Origem no DP</dt>
                <dd>
                  {origin.text}
                  {origin.path && canSeeDp ? (
                    <Link className="finDesc__sub" style={{ display: "block" }} to={origin.path}>
                      Abrir no DP
                    </Link>
                  ) : null}
                </dd>
              </div>
            ) : null}
          </dl>
          {entry.notes ? <p className="dpNotice" style={{ margin: 0 }}>{entry.notes}</p> : null}
          {fromDp ? (
            <p className="dpNotice" style={{ margin: 0 }}>
              Previsão criada pelo DP: o valor e o vencimento acompanham o último cálculo de lá enquanto não houver baixa. Com
              baixa, o valor fica como está e o DP vê a diferença. Não se exclui aqui; cancelar vale só para o que falta pagar.
            </p>
          ) : null}
        </section>

        <section className="dpeSection" aria-labelledby="fin-baixas">
          <h2 id="fin-baixas" className="dpeSection__title">
            {isReceivable ? "Recebimentos" : "Pagamentos"}
          </h2>
          <div className="ecoCard">
            <div className="ecoTableWrap">
              <table className="ecoTable finTable">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Conta</th>
                    <th>Forma</th>
                    <th className="ecoTable__num">Valor</th>
                    <th>Comprovante</th>
                    <th>Registrado por</th>
                    {canWrite ? <th className="ecoTable__num">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {!hasSettlements ? (
                    <tr>
                      <td colSpan={canWrite ? 7 : 6} className="ecoEmpty">
                        Nenhuma baixa registrada.
                      </td>
                    </tr>
                  ) : (
                    entry.settlements.map((s) => (
                      <tr key={s.id}>
                        <td>{formatDateOnly(s.paidAt)}</td>
                        <td>{s.account.name}</td>
                        <td>
                          {PAYMENT_METHOD_LABELS[s.method]}
                          {s.notes ? <div className="finDesc__sub">{s.notes}</div> : null}
                        </td>
                        <td className="ecoTable__num finMoney">{formatCurrency(s.amount)}</td>
                        <td>
                          {s.hasFile ? (
                            <DocumentFileButton
                              token={token}
                              fetchBlob={() => getSettlementFileBlobApi(token, s.id)}
                              fileName={s.fileName}
                              className="ecoBtn ecoBtn--sm"
                            />
                          ) : (
                            <span className="ecoTable__faint">—</span>
                          )}
                        </td>
                        <td className="ecoTable__muted">{s.createdBy.nomeCompleto}</td>
                        {canWrite ? (
                          <td className="ecoTable__num">
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm ecoBtn--danger"
                              onClick={() => setConfirm({ kind: "reverse", settlement: s })}
                            >
                              Estornar
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {entry.installments.length > 1 ? (
          <section className="dpeSection" aria-labelledby="fin-parcelas">
            <h2 id="fin-parcelas" className="dpeSection__title">
              Parcelas do documento
            </h2>
            <div className="ecoCard">
              <div className="ecoTableWrap">
                <table className="ecoTable finTable">
                  <thead>
                    <tr>
                      <th>Parcela</th>
                      <th>Vencimento</th>
                      <th className="ecoTable__num">Valor</th>
                      <th className="ecoTable__num">Em aberto</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.installments.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.id === entry.id ? (
                            <strong>
                              {p.installmentNumber}/{entry.installmentCount} (esta)
                            </strong>
                          ) : (
                            <button
                              type="button"
                              className="ecoLinkBtn"
                              onClick={() => navigate(FIN_ENTRY_PAGE(p.id))}
                            >
                              {p.installmentNumber}/{entry.installmentCount}
                            </button>
                          )}
                        </td>
                        <td>{formatDateOnly(p.dueDate)}</td>
                        <td className="ecoTable__num finMoney">{formatCurrency(p.amount)}</td>
                        <td className="ecoTable__num finMoney">{formatCurrency(p.openAmount)}</td>
                        <td>
                          <span className={`dpBadge ${ENTRY_STATUS_BADGE[p.status]}`}>
                            {statusLabel(p.status, entry.type)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}

        {canWrite && !isCanceled ? (
          <div className="finActions" style={{ justifyContent: "flex-start" }}>
            {hasSettlements ? (
              hasOpen ? (
                <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setCanceling(true)}>
                  Cancelar o restante
                </button>
              ) : null
            ) : (
              <>
                <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setCanceling(true)}>
                  Cancelar lançamento
                </button>
                {fromDp ? null : (
                  <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setConfirm({ kind: "delete" })}>
                    Excluir
                  </button>
                )}
              </>
            )}
          </div>
        ) : null}
      </main>

      {settling ? (
        <SettlementModal
          token={token}
          entry={entry}
          onClose={() => setSettling(false)}
          onSaved={() => {
            setSettling(false);
            load();
          }}
        />
      ) : null}

      {canceling ? (
        <ReasonModal
          title={hasSettlements ? "Cancelar o restante" : "Cancelar lançamento"}
          description={
            hasSettlements
              ? `As baixas já feitas continuam no caixa; os ${formatCurrency(entry.openAmount)} em aberto deixam de ser cobrados.`
              : "O lançamento continua no histórico, marcado como cancelado. Para apagar um lançamento feito por engano, use Excluir."
          }
          confirmLabel="Cancelar lançamento"
          onClose={() => setCanceling(false)}
          onConfirm={async (reason) => {
            await cancelEntryApi(token, entry.id, reason);
            toast.success("Lançamento cancelado.");
            setCanceling(false);
            await load();
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === "delete" ? "Excluir lançamento?" : "Estornar baixa?"}
        message={
          confirm?.kind === "delete"
            ? "O lançamento some das listas e do fluxo de caixa. Use para lançamentos feitos por engano."
            : confirm?.kind === "reverse"
              ? `A baixa de ${formatCurrency(confirm.settlement.amount)} em ${formatDateOnly(confirm.settlement.paidAt)} é apagada e o valor volta a ficar em aberto.`
              : ""
        }
        confirmLabel={confirm?.kind === "delete" ? "Excluir" : "Estornar"}
        tone="danger"
        busy={busy}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
